<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import V2WizardSteps from '@/components/modals/printing/wizard/V2WizardSteps.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { RESERVATION_GUIDE_STEPS } from '@/domain/reservations/reservation-guide';
  import type {
    TReservationGuideScreen,
    TReservationGuideStep,
  } from '@/domain/reservations/reservation-guide';

  /** Title, branch pill, «Cerrar» and the step indicator (hidden on the finish screen). */
  const props = defineProps<{
    screen: TReservationGuideScreen;
    /** Shown only when the restaurant has more than one visible branch. */
    branchName?: string;
    /** «Cerrar» waits while a write is in flight. */
    disabled?: boolean;
  }>();
  const emit = defineEmits<{ (e: 'exit'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const labels = computed(() =>
    RESERVATION_GUIDE_STEPS.map((step: TReservationGuideStep) =>
      tr(`reservations.guide.steps.${step}`)
    )
  );
  const index = computed(() =>
    RESERVATION_GUIDE_STEPS.findIndex((step) => step === props.screen)
  );
  const stepLine = computed(() =>
    index.value >= 0
      ? tr('reservations.guide.step', {
          step: index.value + 1,
          total: labels.value.length,
          name: labels.value[index.value],
        })
      : ''
  );
</script>

<template>
  <header class="reservations-guide-top-bar">
    <div class="bar-main">
      <h1 class="guide-title">{{ $t('reservations.guide.title') }}</h1>
      <span v-if="branchName" class="branch-pill">{{
        $t('reservations.guide.branch', { name: branchName })
      }}</span>
      <PosUiButton
        class="exit-button"
        color="neutral"
        variant="ghost"
        icon="close"
        :disabled="disabled"
        @click="emit('exit')"
        >{{ $t('reservations.guide.exit') }}</PosUiButton
      >
    </div>
    <div v-if="index >= 0" class="bar-steps">
      <V2WizardSteps :steps="labels" :current="index" />
      <!-- The indicator shows dots only below md: name the step in words there. -->
      <p class="step-line">{{ stepLine }}</p>
    </div>
  </header>
</template>

<style scoped lang="scss">
  .reservations-guide-top-bar {
    @apply tw-shrink-0 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 md:tw-px-6;
    // Below md the branch pill takes its own line so the title keeps its room.
    .bar-main {
      @apply tw-flex tw-min-h-14 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-py-1 md:tw-flex-nowrap md:tw-py-0;
    }
    .guide-title {
      @apply tw-m-0 tw-min-w-0 tw-flex-1 tw-truncate tw-text-base tw-font-semibold tw-text-neutral-900 md:tw-text-lg;
    }
    .branch-pill {
      @apply tw-order-3 tw-max-w-full tw-basis-full tw-self-start tw-truncate tw-rounded-full tw-bg-neutral-100 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-neutral-600 md:tw-order-none md:tw-max-w-[40%] md:tw-basis-auto md:tw-self-auto;
    }
    .exit-button {
      @apply tw-shrink-0;
      :deep(.pos-ui-button-label) {
        @apply tw-sr-only sm:tw-not-sr-only sm:tw-inline-flex;
      }
    }
    .bar-steps {
      @apply tw-flex tw-flex-col tw-gap-1 tw-pb-3;
    }
    // Stays in the accessibility tree on md+ (the indicator has no aria-current).
    .step-line {
      @apply tw-m-0 tw-text-xs tw-font-medium tw-text-neutral-600 md:tw-sr-only;
    }
  }
</style>
